/**
 * genieSetupStorage — the per-device slice of the genie setup checklist that has
 * no home on the agent record: whether this genie has been tested live, and which
 * steps the owner has waved off. Written by GenieSetupImmersive, read by
 * GenieDetailPage so the landing decision (checklist vs full workspace) can see
 * completion before choosing a view.
 *
 * All access goes through lsGet/lsSet: Safari private browsing throws on any
 * storage touch, and a page that white-screens is worse than a preference that
 * does not persist.
 */
import { GenieSetupOverrides, GenieSetupStepId } from "@/types/agent/genie-setup";

export const genieTestedKey = (genieId: string): string => `hg-genie-tested-${genieId}`;
export const genieSkippedKey = (genieId: string): string => `hg-genie-skipped-${genieId}`;

export const lsGet = (key: string): string | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};

export const lsSet = (key: string, value: string): void => {
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(key, value);
  } catch {
    // The preference stays in memory for this session.
  }
};

export const readSkippedSteps = (genieId: string): GenieSetupStepId[] => {
  const raw = lsGet(genieSkippedKey(genieId));
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed.filter((v) => typeof v === "string") as GenieSetupStepId[]) : [];
  } catch {
    return [];
  }
};

/** Everything buildGenieSetupState needs beyond the agent record, in one read. */
export const readGenieSetupOverrides = (genieId: string): GenieSetupOverrides => ({
  tested: lsGet(genieTestedKey(genieId)) === "1",
  skipped: readSkippedSteps(genieId),
});
